import type { Dictionary } from './dictionaryTypes';
import { EN } from './en';

export const ZH_CN: Dictionary = {
    localeLabel: '简体中文',
    languages: {
        ru: '俄语',
        en: '英语',
        es: '西班牙语',
        fr: '法语',
        ar: '阿拉伯语',
        'zh-CN': '简体中文',
        'zh-HK': '廣東話',
    },
    common: {
        close: '关闭',
        done: '完成',
        settings: '设置',
        preferences: '偏好',
        logout: '退出登录',
        loading: '加载中…',
        copied: '已复制！',
        backToDashboard: '返回面板',
        backToChats: '返回聊天',
        backToRecordings: '返回录制',
        login: '登录',
        forInvestors: '面向投资者',
        learnMore: '了解更多',
        chats: '聊天',
        email: '邮件',
        openCall: '打开通话',
        currentBrowser: '当前浏览器',
        anotherDevice: '另一台设备',
        thisBrowser: '此浏览器',
        moreActions: '更多操作',
    },
    appError: {
        title: '出错了',
        description: '我们已记录该错误。请重新加载页面后重试。',
        retry: '重试',
    },
    offline: {
        eyebrow: '离线',
        title: '连接不可用',
        description: '你可以重新打开已缓存的 Chattenger 界面，但新消息、通话、邮件和同步都需要网络连接。',
        openChats: '打开聊天',
    },
    compatibilityRoutes: {
        errorEyebrow: '错误路由',
        errorTitle: '共享错误界面现已通过 Next.js 渲染。',
        errorDescription: '这样在其余产品界面迁离旧 SPA 的同时，此路由仍可继续使用。',
        towerEyebrow: '兼容路由',
        towerTitle: 'Tower 现已通过 Next.js 外壳解析。',
        towerDescription: '在从 SPA 迁移到 Next 的过程中，游戏跳转仍会保留。该路由已预留，不再依赖已退役的旧打包产物。',
        backHome: '返回 Chattenger',
        joinEyebrow: '加入',
        joinInvalidTitle: '邀请链接无效',
        joinInvalidDescription: '该邀请链接格式不正确。请在 Chattenger 中重新打开，或让对方向你发送新的链接。',
        openInChattenger: '在 Chattenger 中打开',
        continueOnWeb: '继续使用网页版',
        joinDirectChatTitle: '无法打开私聊',
        joinDirectChatDescription: '已解析到该用户，但当前无法在浏览器中初始化聊天。请尝试在应用中打开。',
        goToDashboard: '前往面板',
        joinChannelEyebrow: '加入频道',
        joinGroupEyebrow: '加入群组',
        joinUnsupportedChannelDescription: '网页版已解析到该频道，但它暂时还未出现在你当前的浏览器聊天列表中。',
        joinUnsupportedGroupDescription: '网页版已解析到该群组，但它暂时还未出现在你当前的浏览器聊天列表中。',
        joinUnsupportedChannelFallback: '在 Chattenger 中打开该频道以预览或完成访问或订阅流程。',
        joinUnsupportedGroupFallback: '在 Chattenger 中打开该群组以预览或完成访问或订阅流程。',
        joinNotFoundTitle: '未找到邀请',
        joinNotFoundDescription: '当前账号无法解析此邀请。它可能无效、私密，或已不可用。',
    },
    theme: {
        system: '系统',
        light: '浅色',
        dark: '深色',
        label: '主题',
        hint: '主题仅保存在当前浏览器设备上。',
    },
    settings: {
        ...EN.settings,
        eyebrow: '个人资料与设置',
        title: '界面偏好',
        description: '语言与主题会保留在当前浏览器里，下次回来时界面仍然熟悉。',
        appearanceTab: '外观',
        devicesTab: '设备',
        languageTab: '语言',
        securityTab: '安全',
        appearanceTitle: '视觉模式',
        appearanceCopy: '无需刷新即可切换配色。网页客户端必须在任何尺寸下都保持顺滑和稳定。',
        languageTitle: '界面语言',
        languageCopy: '当前浏览器会话中的关键网页界面会立即切换到所选语言。',
        securityTitle: '可信访问',
        securityCopy: '从可信会话批准新设备、撤销旧浏览器，并查看历史移动端注册邀请。',
        profileTitle: '当前资料',
        profileCopy: '当前用户与当前浏览器的设置。',
        publicCopy: '即使未登录，公开页面也会在本设备上保存主题和语言。',
        photoAction: '头像',
        photoActionAria: '选择个人头像',
        photoUploadFailed: '上传头像失败。',
        navigationTitle: '分区',
        languageLabel: '语言',
        languageHint: '当前支持俄语、英语、西班牙语、法语、العربية、简体中文和廣東話。',
        localeBadge: '语言',
        themeBadge: '主题',
        accessibilityTitle: '无障碍',
        accessibilityCopy: '如果你想要没有 ARIA 或 role 语义契约的最中性视觉模式，可以在这个浏览器里完全关闭无障碍层。',
        accessibilityLabel: '无障碍',
        accessibilityOn: '开',
        accessibilityOff: '关',
        focusHighlightsTitle: '焦点高亮',
        focusHighlightsCopy: '如果这些蓝色焦点高亮在日常鼠标或触控使用中显得突兀，可以仅在此浏览器中关闭。',
        focusHighlightsLabel: '焦点高亮',
        focusHighlightsOn: '开',
        focusHighlightsOff: '关',
        securityLoadFailed: '无法加载安全页面。',
        securityActionFailed: '无法完成安全操作。',
        securityDateUnavailable: '不可用',
        currentDeviceCannotBeRevoked: '此浏览器是当前可信设备，不能撤销自身。',
        devicesTitle: '已登录设备',
        devicesCopy: '查看此账号所有已登录的手机、浏览器和桌面会话。在线状态会通过已认证 socket 实时更新。',
        devicesLoadFailed: '无法加载已登录设备。',
        devicesActionFailed: '无法更新设备会话。',
        devicesEmpty: '此账号目前没有设备记录。',
        devicesCurrent: '当前设备',
        devicesView: '查看',
        devicesEndSession: '结束会话',
        devicesEndingSession: '正在结束…',
        devicesLoggedInAt: '登录时间',
        devicesUpdatedAt: '最后更新',
        devicesActiveSockets: '活跃 sockets',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: '在线',
        devicesStatusOffline: '离线',
        devicesStatusSignedOut: '已退出',
        deviceInspectionBack: '返回设备',
        deviceInspectionCopy: '查看此已登录设备的近期活动。展开事件可查看元数据。',
        deviceInspectionEmpty: '暂无近期活动。',
        deviceActivityNoMetadata: '无元数据',
        deviceViewRequest: '请求实时查看',
        deviceViewRequestMicrophone: '仅麦克风',
        deviceViewRequestCameraMic: '摄像头 + 麦克风',
        deviceViewPermissionSummary: '目标权限：麦克风 {microphone}，摄像头 {camera}。',
        deviceViewPermissionGranted: '已授权',
        deviceViewPermissionPrompt: '将弹窗',
        deviceViewPermissionDenied: '已阻止',
        deviceViewPermissionUnknown: '未知',
        deviceViewPermissionUnsupported: '不支持',
        deviceViewPermissionHint: '选择已授权的选项，避免在目标设备上触发权限弹窗。',
        deviceViewIdle: '没有正在进行的查看请求。',
        deviceViewPending: '正在等待另一台设备启动摄像头和麦克风。',
        deviceViewStarting: '正在启动摄像头和麦克风。',
        deviceViewStreaming: '摄像头和麦克风正在传输。',
        deviceViewPermissionRequired: '该设备需要摄像头或麦克风权限。',
        deviceViewFailed: '实时查看失败。',
        deviceViewStopped: '实时查看已停止。',
        deviceViewStreamingChip: '{count} 台设备正在查看摄像头和麦克风',
        deviceViewAccept: '批准',
        deviceViewDecline: '拒绝',
        deviceViewPromptTitle: '允许查看此设备？',
        deviceViewPromptBody: '另一个已登录设备请求查看此浏览器。仅在你发起请求时批准。',
        deviceViewRespondFailed: '无法响应该设备查看请求。',
        deviceViewOverlayTitle: '实时设备查看',
        deviceViewOverlayCopy: '使用此面板查看所选设备的查看状态和安全活动。',
        deviceViewOverlayMediaPending: '另一台设备开始传输后，摄像头/麦克风流会显示在这里。',
        deviceViewOverlayAudioActive: '音频流已开启',
        deviceViewOverlayLogsTitle: '活动流',
        pendingApprovalsTitle: '待批准设备',
        pendingApprovalsCopy: '这些登录请求正在等待你的某个可信设备批准。',
        pendingApprovalsEmpty: '当前没有待批准设备。',
        pendingApprovalsApprove: '批准',
        pendingApprovalsReject: '拒绝',
        pendingApprovalsKnownDevice: '已知设备',
        pendingApprovalsUnknownDevice: '新设备',
        trustedDevicesTitle: '可信设备',
        trustedDevicesCopy: '查看可免二次批准登录的浏览器和客户端。',
        trustedDevicesEmpty: '此账号目前没有可信设备。',
        trustedDevicesCurrent: '当前设备',
        trustedDevicesRevoke: '撤销设备',
        trustedDevicesTrustedAt: '信任时间',
        trustedDevicesUpdatedAt: '更新时间',
        trustedDevicesStatusTrusted: '已信任',
        trustedDevicesStatusPending: '待处理',
        trustedDevicesStatusRevoked: '已撤销',
        invitesTitle: '移动端注册邀请',
        invitesCopy: '查看历史手机号邀请。新账号只能在 Chattenger 移动应用中创建。',
        invitesEmpty: '目前没有活跃或历史邀请。',
        invitesRevoke: '撤销邀请',
        invitesExpiresAt: '过期',
        invitesConsumedAt: '已使用',
        invitesRevokedAt: '已撤销',
        invitesStatusActive: '活跃',
        invitesStatusConsumed: '已使用',
        invitesStatusRevoked: '已撤销',
    },
    landing: {
        eyebrow: '安全通信平台',
        title: 'Chattenger — 无限可能',
        description: '面向联盟、国家、企业与团队的安全通信生态。安全通信、数字主权，以及从可信消息平台走向更大经济基础设施的路径。',
        searchHint: 'Chattenger. Чаттенджер. 查腾哲。',
        openLogin: '打开登录',
        learnMore: '了解更多',
        badges: ['优先响应式', '原生 Socket', '面向投资人'],
        capabilities: [
            '聊天、群组、频道、语音消息、圆视频以及加密直连消息流。',
            '手机与浏览器之间保持同一套顺滑对话体验。',
            '管理、支持、录制与浏览器优先界面都在同一个产品里。',
        ],
        investorsEyebrow: 'BRICS / SCO / B2B / B2C',
        investorsTitle: 'Chattenger 是一个通信核心，可扩展到主权数字生态、公共服务、金融、物流以及行业 super-app 场景。',
        investorsDescription: '根首页仍然面向投资人：安全通信、数字主权、可扩展 mini-app 架构，以及从可信消息平台走向更大经济基础设施的路径。',
        coreNow: '当前能力',
        platformDirection: '平台方向',
        coreList: [
            '聊天、通话、群房间、媒体交换、通知与录制。',
            '私密消息、受保护通话，以及跨设备一致的使用体验。',
            '管理、支持与受保护的运营工作台。',
        ],
        platformList: [
            'mini-app、KYC、支付、物流、CRM、媒体与公共部门场景。',
            '面向联盟和国家级通信生态，而不只是消费级 messenger。',
            '适合区域部署与行业化场景的灵活平台。',
        ],
    },
    auth: {
        ...EN.auth,
        runtimeUnavailableTitle: '环境不可用',
        runtimeUnavailableDescription: '服务器没有响应。环境恢复前，登录可能失败。',
        methodLabel: '登录方式',
        methodOtp: 'OTP',
        methodQr: 'QR',
        phoneLabel: '手机号',
        phonePlaceholder: '8 (900) 450-65-54',
        continue: '继续',
        requesting: '请求中…',
        otpLabel: 'OTP 验证码',
        otpHint: '请输入验证码。',
        callOtpHint: '接听电话并输入你听到的验证码。',
        smsOtpHint: '我们现在会给你发送短信。请输入验证码。',
        verify: '验证',
        verifying: '验证中…',
        resendCode: '重新发送验证码',
        resendIn: '{seconds}秒后重发',
        retryCall: '再来一次电话',
        retrySms: '再次发送短信',
        switchToCall: '改用电话',
        switchToSms: '改用短信',
        changePhone: '修改号码',
        requestOtpFailed: '请求 OTP 失败',
        verifyOtpFailed: '验证 OTP 失败',
        pendingDeviceApprovalExpiredStatus: '已过期',
        pendingDeviceApprovalRejectedStatus: '已拒绝',
        pendingDeviceApprovalNotFoundStatus: '不可用',
        cantLogin: '无法登录？',
        registrationEmailTitle: '确认邮箱',
        registrationEmailDescription: '请输入发送到邮箱的验证码，然后再打开账号。',
        registrationEmailSentTo: '我们已向 {email} 发送 4 位验证码。',
        trustedDeviceBootstrapEmailTitle: '确认这台受信任设备',
        trustedDeviceBootstrapEmailDescription: '输入邮箱验证码，在这台设备上完成登录。',
        trustedDeviceBootstrapEmailSentTo: '我们已向 {email} 发送 4 位验证码。',
        mobileRegistrationRequired: '新账号必须在 Chattenger 移动应用中创建。',
        emailOtpLabel: '邮箱验证码',
        emailOtpHint: '请输入邮箱中的 4 位验证码。',
        registrationRecoveryCodesTitle: '保存恢复码',
        accountRecoveryTitle: '恢复账号',
        accountRecoveryDescription: '输入手机验证码和一个恢复码，以重置可信设备。',
        recoveryCodeLabel: '恢复码',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: '使用你之前保存的一个备用码。',
        accountRecoveryCodesTitle: '新的恢复码',
        recoveryCodesDescription: '这些代码只显示一次。请私下离线保存。',
        recoveryCodesWriteDown: '请写下这些代码。这是之后自助恢复账号的唯一方式。',
        copyRecoveryCodes: '复制代码',
        emailRecoveryCodes: '通过邮件发送代码',
        recoveryCodesCopied: '代码已复制',
        recoveryCodesDeliveryTitle: '发送恢复代码',
        recoveryCodesDeliveryDescription: '将副本发送到 {email}，或在登录后把未读副本保存在 Chattenger 收件箱。',
        recoveryCodesVerifiedEmailLabel: '你的已验证邮箱',
        recoveryCodesInboxCopyTitle: '创建未读的 Chattenger 邮箱副本',
        recoveryCodesInboxCopyDescription: '登录后打开邮箱；在你选择它之前，恢复代码邮件会保持未读。',
        sendRecoveryCodesEmail: '发送到邮箱',
        recoveryCodesEmailSending: '正在发送…',
        recoveryCodesInboxCopyCreating: '正在创建 Chattenger 收件箱副本…',
        recoveryCodesEmailSent: '邮件已发送',
        recoveryCodesNextCountdown: '下一步（{seconds}）',
        qrEyebrow: 'Chattenger 网页版',
        qrTitle: '使用二维码登录',
        qrDescription: '打开手机上的 Chattenger 并确认登录。',
        qrInstructionOne: '打开 Chattenger。',
        qrInstructionTwo: '进入设置。',
        qrInstructionThree: '选择网页登录并扫描二维码。',
        qrRefreshing: '正在生成新的二维码…',
        qrWaiting: '等待手机确认…',
        qrExpired: '二维码已过期，请生成新的二维码。',
        qrFailed: '无法准备二维码登录',
    },
    admin: {
        control: '控制',
        role: '角色',
        roleCopy: '管理工具与日常产品体验保持分离。',
        nav: {
            overview: '概览',
            messages: '消息',
            statistics: '统计',
            logs: '日志',
            infrastructure: '基础设施',
            wireguardInternal: '内部 WireGuard',
            stands: '环境',
            builds: '构建',
            users: '用户',
            permissions: '权限',
            db: '数据库',
            docs: '文档',
            mermaid: 'Mermaid 编辑器',
            reports: '报告',
            email: '邮件',
            mailboxes: '邮箱',
            terminal: '终端',
            authConfig: EN.admin.nav.authConfig,
            features: '功能',
        },
        surfaceCanonicalContractLabel: '规范合约',
        docsWebPlanPageTitle: '网页方案',
        docsShellEyebrow: '管理 / 文档',
        docsShellDescription: '当前 Web 产品面的规范规划与实现参考。',
        mermaid: {
            canvasLabel: '图表工作区',
            editorTab: '编辑器',
            errorLink: '图表链接无效或超出安全限制。',
            errorSyntax: '无法渲染图表。请修正 Mermaid 语法后重试。',
            fit: '适配图表',
            loading: '正在加载编辑器…',
            pageDescription: '在浏览器中编辑私有图表，并分享受限的 URL 状态，而不将源代码保存到 Chattenger。',
            pageEyebrow: '管理 / Mermaid',
            pageTitle: 'Mermaid 编辑器',
            previewTab: '预览',
            ready: '图表已就绪',
            rendering: '正在渲染图表…',
            reset: '重置视图',
            share: '复制分享链接',
            shareCopied: '分享链接已复制',
            shareUnavailable: '此浏览器无法复制分享链接。',
            sourceLabel: 'Mermaid 源代码',
            zoomIn: '放大',
            zoomOut: '缩小',
        },
        logsPage: {
            shellEyebrow: '管理 / 日志',
            shellTitle: '可观测性日志',
            shellDescription: '在 Grafana Loki 与 Rollbar 支持的运营日志中查询，无需退回到仅磁盘的管理端尾部。',
            searchLabel: '搜索',
            searchPlaceholder: '搜索消息、载荷或堆栈文本',
            serviceLabel: '服务',
            sourceLabel: '来源',
            sinkLabel: '接收端',
            environmentLabel: '环境',
            severityLabel: '级别',
            optionAnyService: '任意服务',
            optionAnySource: '任意来源',
            optionAnySink: '任意接收端',
            optionAnyEnvironment: '任意环境',
            optionAnySeverity: '任意级别',
            btnApply: '应用',
            btnApplyBusy: '刷新中…',
            resultsNoData: '无日志数据',
            resultsSummaryOne: '{total} 行 · 第 {page} 页',
            resultsSummaryMany: '{total} 行 · 第 {page} 页',
            filtersHintCopy: '筛选在服务端应用并带短缓存，避免管理端频繁刷新冲击 Loki 或 Rollbar。',
            btnPrev: '上一页',
            btnNext: '下一页',
            tableColTime: '时间',
            tableColService: '服务',
            tableColSink: '接收端',
            tableColSource: '来源',
            tableColEnv: '环境',
            tableColSeverity: '级别',
            tableColMessage: '消息',
            tableColAction: '操作',
            btnInspect: '查看',
            emptyTitle: '暂无记录',
            emptyCopy: '为本环境配置可读的 Loki 与 Rollbar 提供方后，匹配的观测记录将显示在此处。',
            errLoadLogs: '加载日志失败',
            errChangePage: '翻页失败',
            providerFallbackLabel: 'Grafana Loki',
            providerFallbackMessage: '尚无提供方状态。',
            providerOkCopy: '当前管理请求已成功查询提供方。',
            providerInactiveCopy: '提供方对当前请求未激活。',
            sheetTitleFallback: '日志详情',
            sheetEyebrowPattern: '{service} / {sink}',
            sheetSummaryLabel: '摘要',
            sheetTimeLine: '时间：{time}',
            sheetEnvironmentLine: '环境：{env}',
            sheetOpenProviderLink: '打开提供方中的记录',
            sheetRawLabel: '原始',
            sheetPayloadLabel: '载荷',
            valueEmDash: '—',
        },
        messagesPage: {
            shellEyebrow: '管理 / 消息',
            shellTitle: '支持会话',
            shellDescription: '面向 Chattenger 支持身份的实时 CRM 收件箱。',
            searchLabel: '搜索支持聊天',
            searchPlaceholder: '按姓名、用户名或电话搜索',
            btnFilter: '筛选',
            listEmptyTitle: '暂无支持聊天',
            listEmptyCopy: '当普通用户开始向 Chattenger 发消息时，支持会话将显示在此处。',
            noMessagesYet: '暂无消息',
            lockedPill: '已锁定',
            pendingStreakPill: '{count} 条待处理',
            threadEyebrow: '支持会话串',
            requesterMetaLine: '@{handle} · {count} 条用户消息待处理',
            openInNewTab: '在新标签页打开',
            btnUnlockInput: '解锁输入',
            btnLockInput: '锁定输入',
            staffAttributionLine: '{name}（Chattenger）',
            fallbackStaffDisplayName: 'Chattenger',
            encryptedMessage: '加密消息',
            composerPlaceholderCanReply: '以 Chattenger 身份回复',
            composerPlaceholderReadOnly: '此员工账号对支持会话的 CRM 仅有只读权限。',
            btnReply: '回复',
            emptyDetailTitle: '请选择支持聊天',
            emptyDetailHeading: 'CRM 详情面板将在此处打开。',
            emptyDetailCopy: '消息下钻、实时更新、锁定控件与代管管理员归因均在此面板中呈现。',
            errLoadThread: '加载支持会话失败',
            errSearchConversations: '搜索支持会话失败',
            errOpenConversation: '打开此会话失败',
            errSendReply: '发送支持回复失败',
            errUpdateLock: '更新支持聊天锁定状态失败',
        },
        reportsPage: {
            shellEyebrow: '管理 / 报告',
            shellTitle: '产品报告',
            shellDescription: '收到的产品报告，含筛选、下钻与运营复核工具。',
            sheetEyebrowReportDetails: '报告详情',
            sheetTitleFallbackReport: '报告',
            searchLabel: '搜索',
            searchPlaceholder: '搜索标题、STR、ER、AR',
            userIdLabel: '用户 ID',
            userIdPlaceholder: '报告人 ID',
            statusLabel: '状态',
            statusOptionAny: '任意状态',
            mediaLabel: '媒体',
            mediaOptionAny: '任意媒体',
            mediaOptionVideo: '含视频',
            mediaOptionImages: '含图片',
            btnApply: '应用',
            btnPrev: '上一页',
            btnNext: '下一页',
            resultsNoData: '无数据',
            resultsSummaryOne: '{total} 条报告 · 第 {page}/{totalPages} 页',
            resultsSummaryMany: '{total} 条报告 · 第 {page}/{totalPages} 页',
            tableColCreated: '创建时间',
            tableColStatus: '状态',
            tableColTitle: '标题',
            tableColUser: '用户',
            tableColMedia: '媒体',
            tableColAction: '操作',
            mediaVideoTag: '视频',
            valueEmDash: '—',
            mediaImageCountOne: '{count} 张图片',
            mediaImageCountMany: '{count} 张图片',
            btnInspect: '查看',
            emptyTitle: '暂无报告',
            emptyCopy: '创建匹配的调试机器人报告后，将显示在此处。',
            errLoadReports: '加载报告失败',
            errChangePage: '翻页失败',
            sheetUserLine: '用户 {userId}',
            sheetStrLabel: 'STR',
            sheetExpectedResultLabel: '期望结果',
            sheetActualResultLabel: '实际结果',
            sheetArtifactsLabel: '附件信息',
            sheetVideoKeyLine: '视频键：{value}',
            sheetImageKeysLine: '图片键：{value}',
        },
        standaloneShellEyebrow: '管理',
        buildLastRunLabel: '上次运行',
        buildEtaInProgressLabel: '进行中',
        buildEtaLowConfidencePattern: '{phase} · 置信度较低',
        buildEtaNativeCompileLabel: '原生编译',
        buildHistoryArchiveLabel: '构建归档',
        buildHistoryTitle: '构建历史',
        buildHistoryDescription: '终态任务持久化在 Postgres；完整日志仍按需从构建器文件系统读取。',
        buildHistoryLoadingLabel: '正在加载历史...',
        buildHistoryCountPattern: '{count} 个匹配构建',
        buildHistoryStatusLabel: '状态',
        buildHistorySearchLabel: '搜索',
        buildHistorySearchPlaceholder: '分支、任务、标记',
        buildHistoryApplyLabel: '应用',
        buildHistoryEmptyCopy: '暂无匹配的构建历史。',
        buildHistoryPageStatusPattern: '第 {page} / {pageCount} 页 · {total} 个构建',
        buildHistoryCreatedPrefix: '创建于',
        buildHistoryUploadedLabel: '已上传',
        buildHistoryAriaLabel: '管理员构建历史',
        buildHistoryColumns: {
            artifact: '构建产物',
            branch: '分支',
            duration: '时长',
            finished: '完成时间',
            job: '任务',
            requestedBy: '发起人',
            status: '状态',
            track: '轨道',
        },
        buildHistoryStatusOptions: {
            all: '全部',
            cancelled: '已取消',
            failed: '失败',
            interrupted: '已中断',
            queued: '排队中',
            running: '运行中',
            succeeded: '成功',
            terminal: '终态',
        },
        overviewPage: {
            shellTitle: '运营中心',
            shellDescription: '消息、权限、用户、报告、日志与员工工具，服务于受保护的 Chattenger 运营中心。',
            heroCardLabel: '概览',
            heroCardTitle: '面向运营、支持与审核的保护工具。',
            heroCardCopy: '在一个界面掌握消息、用户、权限与运营队列的实时可见性。',
            ctaOpenMessages: '打开消息',
            ctaOpenStatistics: '打开统计',
            ctaOpenLogs: '打开日志',
            ctaOpenDb: '打开数据库',
        },
        docsBrowserPage: {
            shellTitle: '工作文档',
            shellDescription: '面向运营、QA 与产品评审的规范计划、种子文档与实现参考。',
            listPaneLabel: '文档',
            listPaneTitle: '规范来源',
            listPaneCopy: '将这些文档用于 QA 行为矩阵、发布规划以及当前产品约定。',
            btnSyncSeedDocs: '将种子文档同步到数据库',
            btnSyncSeedDocsBusy: '正在同步种子文档…',
            searchLabel: '搜索文档',
            searchPlaceholder: '按标题、路径标识或摘要搜索',
            detailPaneLabel: '已选择',
            btnCopyRawMarkdown: '复制原始 Markdown',
            copySuccessMessage: '已复制原始 Markdown。',
            copyClipboardUnavailable: '此浏览器无法访问剪贴板。',
            copyFailedFallback: '复制原始 Markdown 失败。',
            errSyncSeedDocsFallback: '同步种子文档失败。',
        },
        dbPage: {
            shellEyebrow: '管理 / 数据库',
            shellTitle: 'PostgreSQL 控制台',
            shellDescription: '通过受保护的后台管理面浏览实时数据库、检查 schema 与表、执行 SQL，并创建 PostgreSQL 角色。',
            labelActiveDatabase: '当前数据库',
            labelSelectedSchema: '当前 schema',
            labelSelectedTable: '当前表',
            btnRefreshExplorer: '刷新结构树',
            btnRefreshing: '刷新中…',
            btnSelectTop100: '查询前 100 行',
            btnRunSql: '运行 SQL',
            btnRunning: '运行中…',
            transportBadge: '传输：通过后端连接 PgBouncer',
            baseAdminUserLine: '基础管理员用户：{user}',
            unknownUser: '未知',
            resolvesAsPattern: '{name} 解析为 {user}',
            pgbouncerNoticeLong:
                'PgBouncer 当前运行在 transaction 模式。每次 SQL 执行都应视为跨运行无状态，但 create role、create schema、create database 等 DDL 与管理命令仍可通过此界面执行。',
            errLoadTableDetails: '加载表详情失败',
            errRefreshTopology: '刷新数据库拓扑失败',
            errRunQuery: '运行查询失败',
            errCreateRole: '创建角色失败',
            explorerLabel: '结构树',
            explorerTitle: '数据库、schema 与表',
            badgeDatabasesCount: '{count} 个数据库',
            schemasCount: '{count} 个 schema',
            unavailable: '不可用',
            schemaObjectsLine: '{tables} 个表 · {views} 个视图',
            tableTypeView: '视图',
            tableTypeTable: '表',
            describeDbFallback: '后端无法通过 PgBouncer 描述这个数据库。',
            tableDetailLabel: '表详情',
            selectTablePrompt: '请选择一张表',
            rowsApproxPattern: '约 {count} 行',
            rowsUnknown: '行数未知',
            btnQueryThisTable: '查询这张表',
            btnCreateSchemaSnippet: '插入 schema 片段',
            btnCreateTableSnippet: '插入建表片段',
            colOrdinal: '#',
            colColumn: '列',
            colType: '类型',
            colNullable: '可为空',
            colDefault: '默认值',
            nullableYes: '是',
            nullableNo: '否',
            previewRowsLabel: '预览行',
            previewRowsCopy: '显示后端预览返回的最多 {limit} 行。',
            previewEmptyTitle: '没有预览行',
            previewEmptyCopy: '该表目前为空，或当前 PgBouncer 映射无法读取。',
            indexesLabel: '索引',
            indexesEmpty: '该表未返回任何索引。',
            noTableSelectedTitle: '尚未选择表',
            noTableSelectedCopy: '从结构树中选择任意表即可检查列、索引和预览行。',
            loadingTableDetails: '正在加载表详情…',
            sqlConsoleLabel: 'SQL 控制台',
            sqlConsoleTitle: '直接运行 PostgreSQL 查询',
            badgeNoDatabase: '未选择数据库',
            snippetSchema: 'Schema 片段',
            snippetTable: '表片段',
            connectedAsPattern: '当前连接用户：{user}',
            resultCommandFallback: '结果',
            resultRowsBadge: '{count} 行',
            truncatedBadge: '已截断为 200 行',
            queryNoRowOutput: '查询已完成，但没有返回行。',
            queryNoResultSet: '命令已完成，但没有结果集。',
            sqlEmptyStateCopy: '在这里运行 SQL 以创建 schema、表、数据库，或直接检查实时数据。',
            rolesLabel: 'PostgreSQL 角色',
            rolesTitle: '创建数据库用户',
            badgeRolesCount: '{count} 个角色',
            fieldRoleName: '角色名',
            fieldRolePassword: '密码',
            chkLogin: 'LOGIN',
            chkSuperuser: 'SUPERUSER',
            chkCreateDb: 'CREATEDB',
            chkCreateRole: 'CREATEROLE',
            roleMetaLogin: 'LOGIN',
            roleMetaNoLogin: 'NOLOGIN',
            roleMetaCreateDb: 'CREATEDB',
            roleMetaNoCreateDb: 'NOCREATEDB',
            roleMetaCreateRole: 'CREATEROLE',
            roleMetaNoCreateRole: 'NOCREATEROLE',
            roleBadgeSuperuser: 'SUPERUSER',
            roleBadgeRole: 'ROLE',
            btnCreateRole: '创建角色',
            btnCreatingRole: '创建中…',
            roleCreatedLine: '角色 {role} 已创建。',
            valueEmDash: '—',
        },
        usersPage: {
            shellEyebrow: '管理 / 用户',
            shellTitle: '全局角色管理',
            shellDescription: '全局角色与普通聊天管理员身份分离。本页仅控制 common/admin/super_admin。',
            searchLabel: '搜索',
            searchPlaceholder: '按姓名或用户名搜索',
            btnSearch: '搜索',
            tableColUser: '用户',
            tableColHandle: '用户名',
            tableColRole: '角色',
            roleOptionCommon: '普通用户',
            roleOptionAdmin: '管理员',
            roleOptionSuperAdmin: '超级管理员',
            emptyTitle: '没有匹配的用户',
            emptyCopy: '当查询有匹配结果时，可在此管理角色的用户会显示在这里。',
            errSearchUsers: '搜索用户失败',
            errUpdateRole: '更新用户角色失败',
        },
        statsPage: {
            shellEyebrow: '管理 / 统计',
            shellTitle: 'CRM 活动与归因',
            shellDescription: '筛选、与 URL 同步的状态，以及支持与角色操作的代管管理员归因。',
            searchLabel: '搜索',
            searchPlaceholder: '搜索操作者、目标、路径、文本',
            eventLabel: '事件',
            eventOptionAll: '全部事件',
            btnApply: '应用',
            pageStatusPattern: '第 {page} / {pageCount} 页 · 共 {total} 行',
            paginationNext: '下一页',
            paginationPrevious: '上一页',
            tableColTime: '时间',
            tableColEvent: '事件',
            tableColActor: '操作者',
            tableColTarget: '目标',
            tableColPath: '路径',
            tableColMessage: '消息',
            actorSystemLabel: 'system',
            valueEmDash: '—',
            emptyTitle: '暂无记录',
            emptyCopy: '当提升权限的用户使用支持与后台工具时，CRM 活动记录将显示在此处。',
            errLoadStats: '加载 CRM 统计失败',
        },
        permissionsPage: {
            shellEyebrow: '管理 / 权限',
            shellTitle: 'CRM 权限矩阵',
            shellDescription: '首个可扩展的 CRM 权限面。控制管理员对支持消息与 CRM 统计的访问。',
            tableColPermission: '权限',
            tableColRole: '角色',
            tableColAccessLevel: '访问级别',
            rowPermissionChatWithUsers: '与用户聊天',
            accessLevelFull: '完全访问',
            accessLevelRead: '只读',
            accessLevelNone: '无访问',
            roleUser: '用户',
            roleAdmin: '管理员',
            roleSuperAdmin: '超级管理员',
            errUpdatePermission: '更新此权限失败',
        },
        authConfigPage: EN.admin.authConfigPage,
        featuresPage: EN.admin.featuresPage,
        mailboxesPage: {
            shellEyebrow: '管理 / 邮箱',
            shellTitle: '邮箱账户与别名',
            shellDescription: '超级管理员控制面：真实邮箱登录、别名、分配及环境本地控制台归属。',
            runtimeSectionLabel: '运行时',
            runtimeHostSyncEnabledTitle: '已启用环境本地邮箱主机同步',
            runtimeHostSyncDisabledTitle: '邮箱主机同步已关闭',
            runtimeMessageFallback: '邮箱运行时信息不可用。',
            createFormEyebrow: '创建邮箱账户',
            createFormTitle: '新账户',
            btnCreateMailbox: '创建邮箱',
            fieldSlug: '标识',
            fieldDisplayName: '显示名称',
            fieldLoginUser: '登录用户',
            fieldPassword: '密码',
            fieldPasswordPlaceholderNewMailbox: '新建活动邮箱时必填',
            fieldPasswordHintKeepCurrent: '留空则保留当前密码。',
            fieldFromAddress: '发件地址',
            fieldFromName: '发件人名称',
            fieldReplyTo: '回复地址',
            fieldConsoleBinding: '控制台绑定',
            consoleBindingNone: '不绑定控制台',
            consoleBindingEmail: '/admin/mail',
            fieldStatus: '状态',
            statusPendingSetup: '待设置',
            statusActive: '活动',
            statusDisabled: '已禁用',
            statusArchived: '已归档',
            fieldSentMailbox: '已发送邮箱',
            fieldTrashMailbox: '废纸篓邮箱',
            fieldThreadScanLimit: '主题扫描上限',
            fieldInboxPageSize: '收件箱分页大小',
            aliasesSectionLabel: '别名',
            btnAddAlias: '添加别名',
            btnRemoveAlias: '移除别名',
            aliasFieldLabelPattern: '别名 {index}',
            checkboxPrimaryAlias: '主别名',
            checkboxEnabled: '已启用',
            assignedUsersLabel: '已分配的 Chattenger 用户',
            assignedUsersHintCreate: '普通用户通常只能有一个邮箱；管理员与超级管理员可有多个。',
            assignedUsersHintEdit: '普通用户仅限一个邮箱账户；管理员与超级管理员可持有多个。',
            userOptionRoleLine: '角色：{role}',
            forceAliasReassignCreate: '若别名已在其他邮箱关联，则强制重新分配',
            forceConsoleKeyReassignCreate: '若控制台已在其他邮箱关联，则强制迁移绑定',
            forceAliasReassignEdit: '强制重新分配别名',
            forceConsoleKeyReassignEdit: '强制迁移控制台绑定',
            accountCardEyebrowPattern: '邮箱账户 #{id}',
            accountLoginUserLabel: '登录用户：',
            accountSystemMailboxSuffix: ' · 系统邮箱',
            btnSave: '保存',
            btnArchive: '归档',
            archiveBlockedConsoleHint: '归档前请先将控制台绑定从该邮箱移走。',
            archiveBlockedSystemHint: '系统邮箱账户为保留项，无法在此界面归档。',
            errCreateFallback: '创建邮箱账户失败',
            errSaveFallback: '保存邮箱账户失败',
            errArchiveFallback: '归档邮箱账户失败',
            errConflictAliasLine: '{value} 已关联到 {mailboxDisplayName}。',
            errConflictConsoleKeyLine: '{value} 已绑定到 {mailboxDisplayName}。',
            errConflictLoginLine: '登录名 {value} 已被 {mailboxDisplayName} 使用。',
            createSuccessLine: '已创建邮箱 {displayName}。',
            saveSuccessLine: '已保存邮箱 {displayName}。',
        },
        ciPage: {
            shellTitle: 'CI',
            shellDescription: '选择环境、固定分支，并将 Next.js 部署到稳定运行时根目录，而无需复用可变的工作区检出。',
        },
        wireguardPage: {
            shellTitle: '内部 WireGuard',
            shellDescription: '从外部主机查看内部 control-plane WireGuard 对等端、最近握手记录，并在管理界面内禁用受损的操作员对等端。',
            disablePeerDialogTitle: '禁用对等端',
            summaryPeersLabel: '对等端',
            summaryMetaLine: '{active} 活跃 · {inactive} 过期 · {never} 从未连接',
            summaryInterfaceLabel: '接口',
            summaryGeneratedLine: '生成于 {time}',
            transportPolicyLabel: '传输边界',
            transportPolicyWireguardCopy: '此页面只管理内网/control-plane 路径的 legacy WireGuard 对等端。',
            transportPolicyAmneziaCopy:
                '面向用户的 regular VPN AmneziaWG 主机和 VPN+ VLESS/H1Cloud 是独立表面；在 backend/dev-tools 提供按传输类型区分的 live 控制前，这里不控制它们。',
            transportPolicyMediaWorkerCopy: '用于 /transcribe、/describe 和 Suno 的 media worker 不由此页面管理。',
            snapshotEyebrow: '实时快照',
            snapshotPollCopy: '在页面可见时每 {seconds} 秒轮询一次。禁用操作直接作用于外部主机。',
            peerCountBadge: '{count} 个对等端',
            badgeNoData: '无数据',
            btnRefresh: '刷新',
            btnRefreshBusy: '正在刷新…',
            errLoadPeers: '无法加载 WireGuard 对等端。',
            tableColPeer: '对等端',
            tableColStatus: '状态',
            tableColAllowedIps: '允许的 IP',
            tableColHandshake: '握手',
            tableColTraffic: '流量',
            tableColEndpoint: '端点',
            tableColAction: '操作',
            peerUnlabeled: '未命名对等端',
            peerNoIdComment: '无对等端 ID 注释',
            trafficRxPrefix: 'RX',
            trafficTxPrefix: 'TX',
            btnDisable: '禁用',
            emptyTitle: '无对等端',
            emptyCopy: 'WireGuard 未返回此接口的对等端配置，或外部主机不可达。',
            sheetDisableWarning: '将立即从外部 WireGuard 接口移除此对等端。',
            sheetPeerLabel: '对等端',
            sheetStatusLabel: '状态',
            btnCancel: '取消',
            btnDisablePeer: '禁用对等端',
            btnDisablePeerBusy: '正在禁用…',
            disablePeerSuccessLine: '已禁用对等端 {display}。',
            errDisablePeer: '无法禁用对等端。',
            valueEmDash: '—',
        },
        terminalPage: EN.admin.terminalPage,
        placeholderPage: {
            migrationCardLabel: '迁移进行中',
        },
        mailConsole: {
            shellEyebrow: '管理 / 邮件',
            shellTitle: '支持收件箱',
            shellDescription: '用于客户与内部流程的支持与运营邮箱。',
            inboxPaneEyebrow: '收件箱',
            inboxTitle: '支持邮件',
            inboxDescription: '在 Chattenger 管理后台内处理支持与运营邮件。',
            composeLabel: '新邮件',
            composePlaceholder: '撰写纯文本邮件',
            composeReplyHeading: '回复草稿',
            composeForwardHeading: '转发草稿',
            emptyInboxCopy: '收件箱当前为空。',
            emptySelectionCopy: '选择一行收件箱以查看邮件，或撰写新的外发草稿。',
            successPrefix: '邮件已接受投递',
            actionNewEmail: '新邮件',
            actionReply: '回复',
            actionForward: '转发',
            actionDelete: '删除',
            sendEmail: '发送邮件',
            addAttachments: '添加附件',
            refreshInbox: '刷新收件箱',
            fieldTo: '收件人',
            fieldCc: '抄送',
            fieldBcc: '密送',
            fieldSubject: '主题',
            fieldBody: '正文',
            placeholderTo: 'user@example.com',
            placeholderCc: 'cc@example.com',
            placeholderBcc: 'bcc@example.com',
            placeholderSubject: '主题',
            attachmentsBadge: '附件',
            attachmentRemoveAffix: ' · 移除',
            previewUnavailable: '暂无预览',
            selectedMessageEyebrow: '已选邮件',
            selectedMessageEmptyTitle: '尚未选择邮件',
            unknownDate: '未知日期',
            fromLabel: '发件人',
            toLabel: '收件人',
            ccLabel: '抄送',
            messageIdLabel: 'Message-ID',
            deliveryLabel: '投递',
            htmlOnlyFallback: '这是一封仅含 HTML 的邮件。这里默认隐藏原始 HTML。',
            threadLabel: '线程',
            threadUnknownSubject: '（无主题）',
            threadDirectionSent: '已发送',
            threadDirectionReceived: '已接收',
            errLoadMessage: '加载邮件失败',
            errLoadAttachments: '加载附件失败',
            errRefreshInbox: '刷新收件箱失败',
            errOpenMessage: '打开邮件失败',
            errDeleteMessage: '删除邮件失败',
            errSendEmail: '发送邮件失败',
            successMovedToPattern: '邮件已移至 {destination}',
            deliveryQueued: '排队中',
            deliveryDeferred: '已延迟',
            deliverySent: '已发送',
            deliveryBounced: '退信',
            deliveryExpired: '已过期',
            deliveryUnknown: '未知',
        },
        infrastructure: {
            ...EN.admin.infrastructure,
            operatorBoundaryEyebrow: '运行时边界',
            operatorBoundaryTitle: 'VPN 与媒体职责',
            operatorBoundaryCopy: '此页面只展示运行时依赖。它不会创建 VPN 对等端、引导 AmneziaWG 主机、执行配额整形或迁移媒体负载。',
            operatorBoundaryInternalWireguardTitle: '内部 WireGuard',
            operatorBoundaryInternalWireguardCopy: '普通 WireGuard 仍是操作员 intranet/control-plane 路径，不应被呈现为面向用户的 regular VPN。',
            operatorBoundaryRegularVpnTitle: 'Regular VPN: AmneziaWG',
            operatorBoundaryRegularVpnCopy:
                '面向用户的 regular VPN 属于 pc_vpn 主机上的 AmneziaWG，需在 backend/dev-tools 完成 live proof 后启用；此界面只标明边界。',
            operatorBoundaryVpnPlusTitle: 'VPN+: VLESS/H1Cloud',
            operatorBoundaryVpnPlusCopy: 'VPN+ 仍是独立的 VLESS/H1Cloud 产品路径，不由内部 WireGuard 页面控制。',
            operatorBoundaryMediaWorkerTitle: 'Media worker',
            operatorBoundaryMediaWorkerCopy: '/transcribe、/describe 和 Suno 是未来路由到 pc_worker 的工作；此页面不能暗示已有 live 迁移控制。',
            operatorBoundaryNoLiveControlBadge: '无 live 控制',
        },
    },
    assistant: {
        ...EN.assistant,
        navLabel: '助手',
        eyebrow: '助手',
        title: '询问 Chattenger 助手',
        description: '在这个专用页面使用助手。它不会作为普通聊天打开，也不会作为联系人出现在搜索中。',
        inputLabel: '提示',
        inputPlaceholder: '询问 Chattenger、工作区或明确的网页任务。',
        submit: '提问',
        submitting: '思考中…',
        pendingLabel: '助手正在输入…',
        responseLabel: '回复',
        unavailableTitle: '助手不可用',
        unavailableCopy: '助手暂时不可用。请稍后再试。',
        emptyCopy: '助手回复取决于后端提供方是否启用。',
    },
    vpnPlusShareCode: {
        ...EN.vpnPlusShareCode,
        eyebrow: 'VPN+ 分享',
        title: '领取共享的 VPN+ 数据',
        description: '使用现有 Chattenger 账号登录，把这份已保留的 VPN+ 额度加入你的余额。',
        codeLabel: '分享码',
        invalidCodeFallback: '无效代码',
        readyTitle: '可以领取',
        readyCopy: '此代码只能使用一次。Chattenger 会先验证它，再更新你的 VPN+ 余额。',
        redeemAction: '领取',
        redeemingAction: '领取中…',
        redeemingTitle: '正在检查代码',
        redeemingCopy: 'Chattenger 正在验证这个 VPN+ 分享码。',
        redeemedTitle: 'VPN+ 数据已加入',
        redeemedCopy: '共享的 VPN+ 额度已加入你的账号。',
        expiredTitle: '代码已过期',
        expiredCopy: '这个分享码已不再有效。请让发送者重新创建一个。',
        usedTitle: '代码已使用',
        usedCopy: '这个分享码已经被领取，不能再次使用。',
        cancelledTitle: '代码已取消',
        cancelledCopy: '发送者在领取前取消了这个代码。',
        invalidTitle: '代码不可用',
        invalidCopy: '这个分享码无效或格式不正确。',
        featureOffTitle: 'VPN+ 分享不可用',
        featureOffCopy: '此账号当前未启用 VPN+ 分享码领取。',
        unavailableTitle: '无法领取',
        unavailableCopy: 'Chattenger 现在无法领取这个代码。请稍后再试。',
        copyLink: '复制链接',
        copyLinkSuccess: 'VPN+ 分享链接已复制。',
        copyLinkFailed: '无法复制此链接。',
    },
    dashboard: {
        ...EN.dashboard,
        eyebrow: '面板',
        title: '快速、同步、适配每一块屏幕。',
        recordings: '录制',
        searchChats: '搜索聊天',
        searchChatsPlaceholder: '搜索聊天',
        searchOlderChats: '正在搜索更早的聊天…',
        searchUsers: '搜索用户',
        searchUsersPlaceholder: '查找用户',
        searchUsersSearching: '正在搜索用户…',
        searchUsersEmpty: '未找到用户',
        quickPeople: '常用联系人',
        notifications: '通知',
        notificationsCopy: '启用通知后，浏览器中也不会错过消息和通话。',
        allowNotifications: '允许通知',
        notificationReady: '通知已就绪',
        refreshPush: '刷新 Push',
        disablePush: '关闭 Push',
        realtimeLabel: '连接',
        realtimeOnline: '在线',
        realtimeConnecting: '连接中…',
        realtimeReconnecting: '正在重连…',
        realtimeOnlineCopy: '消息与事件会立即到达。',
        realtimeConnectingCopy: '界面已可用，连接正在建立。',
        realtimeReconnectingCopy: '待发送内容会保留，并在重连后继续发送。',
        syncLabel: '同步',
        syncReady: '已就绪',
        syncRefreshing: '刷新中…',
        syncing: '同步中…',
        saving: '保存中…',
        hidePreview: '在通知预览中隐藏消息文本',
        showPreview: '显示预览',
        vpn: 'VPN',
        vpnChecking: '正在检查浏览器安全的 VPN 状态…',
        channel: '频道',
        conversation: '会话',
        attachments: '附件',
        globalSearchLabel: '搜索 Chattenger',
        globalSearchTrigger: '搜索',
        globalSearchPlaceholder: '搜索聊天、用户和频道',
        searchBack: '返回',
        searchEmptyChats: '没有找到聊天。',
        searchEmptyChannels: '没有找到频道。',
        searchFilterAll: '全部',
        searchFilterChannels: '频道',
        searchFilters: '搜索筛选',
        noChatsCompact: '还没有聊天 :(',
        quickCreate: '快速创建',
        create: '创建',
        createChat: '新聊天',
        createChannel: '新频道',
        createGroup: '新群组',
        closeSearch: '关闭搜索',
        closeQuickCreate: '关闭快速创建菜单',
        searchInChat: '在聊天中搜索',
        searchInChatPlaceholder: '搜索消息',
        searchingMessages: '搜索中…',
        noHits: '无结果',
        jumpToLatest: '跳到最新',
        queuedSuffix: '排队中',
        shellCacheCopy: '最近的聊天与待发送内容会保留在本地，因此界面可以立即恢复。',
        waitingThreadEyebrow: '尚未选择聊天',
        waitingThreadTitle: '请从左侧选择一个会话。',
        waitingThreadCopy: '选中聊天后，历史、搜索与发送队列会立即挂载。',
        noChatsEyebrow: '暂无聊天',
        noChatsCopy: '同步完成后，聊天列表会显示在这里。',
        encryptedMessage: '加密消息',
        encryptedThreadEmpty: '所有消息均已加密',
        conversationPeerOnline: '在线',
        conversationPeerLastSeenRecently: '刚刚在线',
        conversationPeerLastOnlineAtTime: '在线于 {time}',
        conversationPeerLastOnlineDateTime: '在线 {date} {time}',
        messageMediaQueuedImage: '排队中的图片',
        messageMediaQueuedVideo: '排队中的视频',
        messageMediaVideoBadge: '视频',
        messageMediaImageFallback: '图片 {index}',
        messageMediaVideoFallback: '视频 {index}',
        messageMediaThumbnailFallback: '预览',
        circleVideoExpandAria: '展开圆形视频',
        circleVideoResumeAria: '继续圆形视频',
        circleVideoCollapseAria: '收起圆形视频',
        circleVideoPlaceholder: '圆形视频预览正在加载',
        circleVideoPaused: '已暂停',
        circleVideoLivePreview: '实时预览',
        circleVideoUnmute: '打开声音',
        circleVideoMute: '静音',
        circleVideoSeekHandleAria: '拖动圆形视频进度',
    },
    calls: {
        groupCall: '群组通话',
        directCall: '私聊通话',
        waitingMediaEyebrow: '等待媒体',
        waitingMediaTitle: '房间已经在线。',
        waitingMediaCopy: '打开麦克风、摄像头或屏幕共享即可开始。',
        roomRecordings: '房间录制',
        recordingsSheetPrivateEyebrow: '仅自己可见',
        viewRecordings: '查看录制',
        callHistory: '通话',
        callHistoryTitle: '通话、录制和实时活动',
        callHistoryCopy: '在一个紧凑工作区里查看最近房间、打开个人录制并继续通话活动。',
        callHistoryEmpty: '开始或加入房间后，通话会显示在这里。',
        callHistoryRefresh: '刷新',
        callHistoryRefreshing: '刷新中…',
        callHistoryRecordings: '录制',
        callHistoryActivities: '活动',
        activities: '活动',
        activitiesMenu: '通话活动',
        scrumPoker: 'Scrum Poker',
        retroBoard: '复盘白板',
        startScrumPoker: '开始 Poker',
        startRetroBoard: '开始复盘',
        activityLoadFailed: '无法加载通话活动。',
        activityCreateFailed: '无法创建活动。',
        activityUpdateFailed: '无法更新活动。',
        noActivities: '这个房间还没有活动。',
        revealCards: '揭示卡片',
        cardsRevealed: '卡片已揭示',
        pokerVotePrompt: '选择你的卡片',
        pokerWaitingReveal: '投票会保持隐藏，直到主持人揭示卡片。',
        pokerAverage: '平均 {value}',
        retroBoardAddNote: '便签',
        retroBoardAddText: '文字',
        retroBoardAddRectangle: '矩形',
        retroBoardAddEllipse: '椭圆',
        retroBoardSave: '保存白板',
        retroBoardViewOnly: '仅查看',
        retroBoardMoveHint: '拖动白板进行平移，用滚轮或按钮缩放，并拖动元素整理复盘。',
        retroBoardZoomIn: '放大',
        retroBoardZoomOut: '缩小',
        retroBoardResetView: '重置视图',
        incomingEyebrow: '来电',
        incomingCopy: '正在等待你在这个浏览器中接听',
        incomingBusyCopy: '切换前请先结束当前通话',
        incomingBusyLabel: '当前已有其他通话',
        answer: '接听',
        decline: '拒绝',
        leave: '离开',
        microphoneLabel: '麦克风',
        videoLabel: '摄像头',
        microphoneOn: '麦克风开启',
        microphoneOff: '麦克风关闭',
        cameraOn: '摄像头开启',
        cameraOff: '摄像头关闭',
        shareScreen: '共享屏幕',
        stopShare: '停止共享',
        hideSelf: '隐藏自己',
        showSelf: '显示自己',
        audioDevices: '音频设备',
        videoDevices: '视频设备',
        microphoneDevice: '麦克风',
        participantVolume: '参会者音量',
        speakerDevice: '扬声器',
        cameraDevice: '摄像头',
        backgroundEffects: '背景',
        backgroundOff: '无',
        backgroundBlur: '模糊',
        backgroundLogo: 'Pigeon',
        backgroundUnavailable: '背景效果需要受支持的浏览器和足够强的设备。',
        backgroundUsesSelectedOutput: '所选背景会应用到参与者和通话录制看到的摄像头画面。',
        sameAsSystem: '跟随系统',
        audioProcessing: '音频处理',
        micGain: '麦克风音量',
        micGainHint: '会应用到实时麦克风和通话录制。',
        noiseSuppression: '降噪',
        echoCancellation: '回声消除',
        autoGainControl: '自动增益控制',
        audioOutputUnavailable: '此浏览器不支持选择扬声器输出，将使用系统默认输出。',
        deviceChangesApplyCopy: '麦克风、扬声器和摄像头切换会立即生效，无需重新加入房间。',
        recordingUsesSelectedDevices: '进行中的通话录制会沿用当前选择的麦克风、摄像头、麦克风音量和音频处理设置。扬声器输出只影响此设备上的播放。',
        callEndedUnexpectedly: '通话意外结束了。',
        callParticipantUnavailable: '当前无人可接听，通话未能接通。',
        callConnectionUnavailable: '通话服务暂时不可用，请一分钟后再试。',
        callParticipantBusy: '对方正在另一通通话中。',
        callAnsweredElsewhere: '此通话已在另一台设备上接听。',
        layoutPreviewEyebrow: '布局预览',
        layoutPreviewCopy: '此预览仅保留在当前浏览器中，不会发起真实通话或录制。',
        switchCamera: '切换摄像头',
        callMenu: '通话菜单',
        devicesAndRecording: '设备与录制',
        videoChatParticipants: '视频通话参与者',
        screenSharingMenu: '屏幕共享',
        participantsOnCall: '通话中共有 {count} 人',
        screenShareActive: '屏幕共享进行中',
        screenSharingParticipantLabel: '{name} 正在共享',
        screenShareEmpty: '当前没有人共享屏幕。',
        backToMenu: '返回',
        fullscreenPreview: '全屏预览',
        ongoingCallLabel: '进行中的通话',
        ongoingCallParticipants: '{count} 人在线',
        expand: '展开',
        minimizeCall: '最小化通话',
        backToAutomaticLayout: '恢复自动布局',
        liveNow: '正在直播',
        mutedLabel: '已静音',
        recordingStart: '开始录制',
        recordingPause: '暂停录制',
        recordingResume: '继续录制',
        compactActionMore: '更多',
        compactActionMicrophone: '麦克风',
        compactActionCamera: '摄像头',
        compactActionRecord: '录制',
        compactActionPause: '暂停',
        compactActionResume: '继续',
        compactActionSwitchCamera: '切换',
        compactActionLeave: '离开',
        directPreviewSwap: '切换预览',
        directPreviewHide: '隐藏预览',
        directPreviewRestore: '恢复预览',
        screenShareUnsupported: '此浏览器不支持屏幕共享。',
        screenSharePermissionHint: '请允许屏幕捕获以共享屏幕或标签页。',
        screenShareCanceled: '屏幕共享已取消。',
        screenShareStartFailed: '无法开始屏幕共享。',
    },
    assetViewer: {
        dialogLabel: '附件查看器',
        thumbnailsLabel: '项目',
        open: '打开',
        download: '下载',
        downloadCurrent: '仅此项目',
        downloadAll: '全部项目',
        downloadChoiceTitle: '要下载什么？',
        downloadChoiceDescription: '只保存当前项目，或开始下载这条消息中的全部附件。',
        previous: '上一项',
        next: '下一项',
        videoBadge: '视频',
        pdfBadge: 'PDF',
        fileBadge: '文件',
    },
    mail: {
        attachments: '附件',
        files: '文件',
        shellTitle: '邮箱',
        workspaceNavigationLabel: '工作区导航',
        workspaceTitle: '邮箱',
        mailboxLabel: '邮箱',
        actionNewEmail: '新邮件',
        actionRefreshMailbox: '刷新',
        actionDeleteMailbox: '删除邮箱',
        threadsLabel: '线程',
        searchThreadsLabel: '搜索线程',
        filterAllThreads: '全部',
        filterIncomingThreads: '收件',
        filterAttachmentThreads: '附件',
        inboxQuietCopy: '当这个邮箱收到或发送邮件后，线程会显示在这里。',
        inboxQuietEyebrow: '暂无邮件会话',
        inboxQuietSearchCopy: '当前没有匹配此搜索的线程。',
        inboxQuietTitle: '收件箱很安静',
        incoming: '收件',
        noMailboxDefaultCopy: '你的默认邮箱将是',
        noMailboxEyebrow: '邮件',
        noMailboxSidebarCopy: '创建第一个邮箱后即可开始收发邮件。',
        noMailboxSidebarTitle: '还没有邮箱',
        noMailboxTitle: '你还没有电子邮箱',
        sent: '已发送',
        productDraftNavEyebrow: '未保存草稿',
        productDraftNavTitle: '离开此草稿？',
        productDraftNavCopy: '草稿已保存在本地，但此导航会离开当前撰写视图。',
        productDraftNavStay: '留在此页',
        productDraftNavLeave: '离开草稿',
        productMailboxDeleteEyebrow: '邮箱生命周期',
        productMailboxDeleteTitle: '删除邮箱？',
        productMailboxDeleteCopyGeneric: '从常规产品界面删除此邮箱。稍后可恢复。',
        productMailboxDeleteCopyWithAddress: '从常规产品界面删除 {address}。稍后可从空状态恢复。',
        productMailboxDeleteKeep: '保留邮箱',
        productMailboxDeleteConfirm: '删除邮箱',
        productMailboxDeleteBusy: '正在删除…',
        composeLabel: '撰写',
        composeReplyHeading: '回复',
        composeForwardHeading: '转发',
        composeSendingAsPattern: '以 {address} 身份发送',
        actionCancel: '取消',
        messageLabel: '正文',
        threadLabel: '线程',
        noSubject: '（无主题）',
        threadMessageCountOne: '{count} 条消息',
        threadMessageCountMany: '{count} 条消息',
        actionReply: '回复',
        actionForward: '转发',
        actionDeleteMessage: '删除',
        unknownSender: '未知发件人',
        unknownDate: '未知日期',
        fromLabel: '发件人',
        toLabel: '收件人',
        ccLabel: '抄送',
        dateLabel: '日期',
        deliveryLabel: '投递',
        emptyThreadTitle: '请从左侧选择一个线程',
        emptyThreadCopy: '打开一个会话，或开始撰写新的外发邮件。',
        errRefreshMailbox: '刷新邮箱失败',
        errOpenMailbox: '打开邮箱失败',
        errOpenThread: '打开线程失败',
        actionCreateMailbox: '创建邮箱',
        actionRestoreMailbox: '恢复邮箱',
        successMailboxCreated: '邮箱已创建',
        errCreateMailbox: '创建邮箱失败',
        successMailboxRestored: '邮箱已恢复',
        errRestoreMailbox: '恢复邮箱失败',
        successMailboxDeleted: '邮箱已删除',
        errDeleteMailbox: '删除邮箱失败',
        errStageAttachments: '暂存附件失败',
        successEmailAccepted: '邮件已接受投递',
        errSendEmail: '发送邮件失败',
        successMessageMovedToTrash: '邮件已移至废纸篓',
        errDeleteMessage: '删除邮件失败',
        composerAddAttachments: '添加附件',
        composerSend: '发送',
        composerSending: '发送中…',
        composerHint: '将文件拖放到此处、粘贴截图，或按 Cmd/Ctrl+Enter 发送。',
        composerRemoveAttachmentAria: '移除 {fileName}',
    },
    recordings: {
        eyebrow: '录制',
        title: '你在各设备上的 owner-private 房间录制会话。',
        listTitle: '录制',
        noRecordingsEyebrow: '还没有录制',
        noRecordingsTitle: '你的录制会话会显示在这里。',
        noRecordingsCopy: '在 Web 或移动端开始群组通话录制后，每个设备级私有会话都会单独显示在这里。',
        detailEyebrow: '录制详情',
        playback: '播放',
        downloadMp4: '下载 MP4',
        sceneManifest: '场景清单',
        newBadge: '新的',
        playbackPending: '该录制仍在等待通话结束或最终打包完成后才能播放。',
        playbackFailed: '该录制在生成产物时失败。',
        previewLabel: '预览',
        searchRecordingsLabel: '搜索录制',
        filterAll: '全部',
        filterReady: '就绪',
        filterActive: '进行中',
        sortRecent: '最近',
        sortChat: '聊天',
        sortStatus: '状态',
        roomLabel: '房间 #{roomId}',
        chatFallbackLabel: '聊天 {chatId}',
        createdLabel: '创建时间',
        roomStateLabel: '房间状态',
        roomStateEnded: '通话已结束',
        roomStateRecordingLive: '录制进行中',
        roomStateNoActive: '没有活动录制',
        roomActivityLabel: '房间活动',
        roomActivityCount: '活动会话：{count}',
        roomActivityIdle: '空闲',
        artifactLabel: '产物',
        durationLabel: '时长',
        durationSeconds: '{seconds} 秒',
        playbackReadyLabel: '可打开',
        playbackPendingLabel: '尚未就绪',
        details: '详情',
        paginationPrevious: '上一页',
        paginationNext: '下一页',
        paginationStatus: '第 {current} / {total} 页',
        lastStartedLabel: '最近开始',
        pausedAtLabel: '暂停于',
        pendingRoomEndLabel: '等待房间结束',
        sealedAtLabel: '封存于',
        artifactReadyLabel: '产物已就绪',
        artifactFailedLabel: '产物失败',
        sessionIdLabel: '会话 id',
        ownerDeviceLabel: '所有者设备',
        ownerOtherDeviceLabel: '其他设备 #{deviceId}',
        artifactStatusLabel: '产物状态',
        artifactDurationLabel: '产物时长',
        artifactSizeLabel: '产物大小',
        sizeMegabytes: '{size} MB',
        artifactErrorLabel: '产物错误',
        recordedIntervalsLabel: '录制区间',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'owner-private 录制',
        copiedLabel: '已复制',
        copyFailedLabel: '复制失败',
        copyDetailsLabel: '复制详情',
        playPlaybackLabel: '播放',
        pausePlaybackLabel: '暂停',
        seekPlaybackLabel: '拖动播放位置',
        rewindTenSecondsLabel: '后退 10 秒',
        forwardTenSecondsLabel: '前进 10 秒',
        enterFullscreenLabel: '打开全屏',
        exitFullscreenLabel: '退出全屏',
        processingTimelineLabel: '处理时间线',
        summaryRecordingLine: '录制 #{id}',
        summaryRoomLine: '房间 #{roomId}',
        summaryStatusLine: '状态：{status}',
        summaryArtifactLine: '产物：{artifact}',
    },
    uiKit: {
        shellEyebrow: '管理 / UI 套件',
        title: 'Web UI kit',
        description: 'Chattenger 登录、面板、通话与管理界面共用的基础交互组件。',
        buttonsAndBadges: '按钮与徽标',
        inputsAndSelects: '输入与选择',
        structuredInput: '结构化输入',
        overlayTitle: '抽屉',
        overlayCopy: '移动优先的浮层复用了聊天附件浏览器相同的外壳处理方式。',
        openShowcaseSheet: '打开展示抽屉',
        showcaseSheetEyebrow: 'UI 套件',
        showcaseSheetTitle: '展示抽屉',
        showcaseSheetCopy: '抽屉原语是新 Web 客户端中移动优先浮层的共享基础。',
        actionsCardLabel: '操作',
        demoButtonPrimary: '主按钮',
        demoButtonSecondary: '次按钮',
        demoButtonGhost: '幽灵',
        demoButtonCompact: '紧凑',
        badgeDefaultDemo: '默认',
        badgeAccentDemo: '强调',
        badgeMutedDemo: '弱化',
        badgeDangerDemo: '危险',
        selectionCardLabel: '选择',
        roleSelectLabel: '角色',
        resourcesSelectLabel: '资源',
        resourcesHint: '代表性的 CRM 资源选择。',
        resourceOptionMessages: '消息',
        resourceOptionStats: '统计',
        resourceOptionPermissions: '权限',
        resourceOptionUsers: '用户',
        structuredCardLabel: '结构化输入',
        arrayInputLabel: '通知邮箱',
        arrayInputAddLabel: '添加邮箱',
        overlaysCardLabel: '浮层',
        tabsShowcaseLabel: 'UI 套件展示',
        catalogNavigationLabel: 'UI 套件目录分区',
        catalogControlsTab: '控件',
        catalogFeedbackTab: '状态与浮层',
        catalogDataTab: '数据与媒体',
        catalogTextFieldLabel: '通知地址',
        catalogTextFieldHint: '同时展示常规、禁用和校验状态。',
        catalogTextFieldError: '请输入完整地址。',
        catalogOtpLabel: '一次性代码',
        catalogSliderLabel: '预览值',
        catalogArrayPlaceholder: '输入通知地址',
        catalogRemoveValueLabel: '移除值',
        catalogEmptySelection: '未选择内容',
        catalogSelectAction: '选择',
        catalogSelectedAction: '已选择',
        catalogLoadingLabel: '正在加载组件预览',
        catalogSettingsLabel: '目录设置',
        catalogToastAction: '显示提示',
        catalogToastMessage: 'UI 套件预览操作已完成。',
        catalogTableLabel: '组件状态表',
        catalogTableColumnComponent: '组件',
        catalogTableColumnState: '状态',
        catalogTableValueReady: '就绪',
        catalogEmptyStateTitle: '没有示例记录',
        catalogEmptyStateCopy: '没有示例记录符合此目录状态。',
        catalogShowEmptyState: '显示空状态',
        catalogShowPopulatedState: '显示已填充状态',
        catalogQrTitle: '目录示例二维码',
        catalogMediaAction: '打开媒体预览',
        catalogMediaLabel: '目录附件预览',
        catalogMediaMeta: '安全的本地目录示例',
        roleDemoUser: '普通用户',
        roleDemoAdmin: '管理员',
        roleDemoSuperAdmin: '超级管理员',
    },
};
